// First, before anything can redirect: captures launch parameters from the
// URL while they are still on it (trap T2).
import './launch-params';
import { createApp } from 'vue';
import './brand-font.css';
import { createRouter, createWebHistory } from 'vue-router';
import App from './App.vue';
import PairingView from './views/PairingView.vue';
import PlaybackView from './views/PlaybackView.vue';
import { isPaired } from './api/tokenStore';
import { setAuthLossHandler } from './api/client';
import { startTitleHeartbeat } from './telemetry/title-heartbeat';
import { startUpdateChecks } from './update/sw-update';

declare const __PLAYER_BUILD_VERSION__: string;
export const BUILD_VERSION = __PLAYER_BUILD_VERSION__;

// A provisioned pairing code (?pairingCode=…, from the kiosk's boot config)
// is captured once at startup: router redirects — especially the auth-loss
// bounce off stale tokens after a device revoke — drop the query string, so
// reading it later from the route is not reliable.
export const PROVISIONED_PAIRING_CODE: string | null = new URLSearchParams(
  window.location.search,
).get('pairingCode');

const router = createRouter({
  history: createWebHistory(),
  routes: [
    { path: '/', name: 'playback', component: PlaybackView },
    { path: '/pair', name: 'pair', component: PairingView },
  ],
});

router.beforeEach((to) => {
  // Query is carried along so a provisioned ?pairingCode=… (dropped into the
  // kiosk's boot config) survives the redirect onto the pairing view.
  if (!isPaired() && to.name !== 'pair') return { name: 'pair', query: to.query };
  if (isPaired() && to.name === 'pair') return { name: 'playback' };
  return true;
});

setAuthLossHandler(() => {
  void router.replace({ name: 'pair' });
});

// App-wide, so the pairing screen counts as a live renderer too (D15).
startTitleHeartbeat();

// New player builds reach the screen within ~30 min, at an item boundary (D14).
// The dev server serves no worker.
if (import.meta.env.PROD) startUpdateChecks();

createApp(App).use(router).mount('#app');
